const formatCurrency = (currency: number): string => {
  if (isNaN(currency)) return "0"; // Return a default value if input is NaN

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "NGN",
  });

  let formattedCurrency = formatter.format(currency).substring(3); // Remove "NGN"

  // Handle negative values
  if (
    formattedCurrency.startsWith("N") &&
    !formattedCurrency.startsWith("NaN")
  ) {
    formattedCurrency = formattedCurrency.substring(1); // Remove "N"
    formattedCurrency = "-" + formattedCurrency;
  }

  // Remove decimals if ".00"
  if (formattedCurrency.includes(".00")) {
    formattedCurrency = formattedCurrency.substring(
      0,
      formattedCurrency.length - 3,
    );
  }

  return formattedCurrency;
};

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatDateAndTime(createdAt: string | Date): string {
  const date = new Date(createdAt);

  function getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return `${day}th`;
    const suffixes: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
    return `${day}${suffixes[day % 10] || "th"}`;
  }

  const day = getOrdinalSuffix(date.getDate());
  const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
    date,
  );
  const year = date.getFullYear();
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .toLowerCase();

  return `${day} ${month}, ${year} ${time}`;
}

type CurrencySymbol = string;

type CurrencyWithSymbol = {
  symbol: string;
  exchange_rate: number | string;
};

const getRateBySymbol = (
  symbol: CurrencySymbol,
  currencies?: CurrencyWithSymbol[],
): number | null => {
  if (!currencies || currencies.length === 0) return null;
  const normalized = symbol.trim();
  const match = currencies.find((currency) => currency.symbol === normalized);
  if (!match) return null;
  const rate = Number(match.exchange_rate);
  return Number.isFinite(rate) ? rate : null;
};

export const convertCurrency = (
  amount: number,
  fromRate: number | CurrencySymbol,
  toRate: number | CurrencySymbol,
  currencies?: CurrencyWithSymbol[],
): number => {
  if (typeof fromRate === "number" && typeof toRate === "number") {
    return (amount / fromRate) * toRate;
  }

  if (typeof fromRate === "string" && typeof toRate === "string") {
    const fromResolved = getRateBySymbol(fromRate, currencies);
    const toResolved = getRateBySymbol(toRate, currencies);

    if (fromResolved === null || toResolved === null) return 0;
    return (amount / fromResolved) * toResolved;
  }

  return 0;
};

export const convertCurrencyAmount = (
  amount: number,
  fromCurrency: { exchange_rate: number | string },
  toCurrency: { exchange_rate: number | string },
): number => {
  const fromRate = Number(fromCurrency?.exchange_rate);
  const toRate = Number(toCurrency?.exchange_rate);

  if (
    !Number.isFinite(amount) ||
    !Number.isFinite(fromRate) ||
    !Number.isFinite(toRate) ||
    fromRate === 0
  ) {
    return 0;
  }

  const converted = (amount / fromRate) * toRate;
  return Number(converted.toFixed(2));
};

export const isInReactNativeWebView = (): boolean =>
  typeof window !== "undefined" &&
  Boolean(
    (window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView,
  );

export { formatCurrency, formatDate };

// utils/utils.ts

export const calculateDGCoinAmount = (
  amount: number | string,
  currencyId?: number,
  currencies: any[] = [],
  dgCoinCurrency?: any,
): number => {
  const numericAmount = Number(amount);
  if (isNaN(numericAmount) || numericAmount <= 0) return 0;

  const fromCurrency =
    currencies.find((c: any) => c.id === currencyId) || dgCoinCurrency;

  if (!fromCurrency || !dgCoinCurrency) {
    return numericAmount;
  }

  // Pure numeric conversion (e.g. 10 USD -> 500 DG Coins)
  return convertCurrencyAmount(numericAmount, fromCurrency, dgCoinCurrency);
};

export const getRequestedAmount = (
  type: string,
  additionalInputValue?: string | number,
  isPercentPlan?: boolean,
): number => {
  // Only airtime and electric use additionalInputValue as a monetary amount
  const monetaryTypes = ["airtime", "electric"];

  if (monetaryTypes.includes(type) || isPercentPlan) {
    const parsed = Number(additionalInputValue);
    return !isNaN(parsed) && parsed > 0 ? parsed : 0;
  }

  return 0;
};
